import { FC, useMemo, useState } from 'react';

import { createStaticDataProvider } from '@hh.ru/magritte-common-data-provider';

import useTrls from 'hooks/translation/useTrls';

import { fetchWizardVacancySearch } from 'components/CampaignCreationWizard/VacancySelectStep/components/VacancyListTab/VacancyFiltersPanel/fetchWizardVacancySearch';
import { SingleFilter } from 'components/common/SingleFilter/SingleFilter';

import {
    selectWizardSelectedFilterValues,
    selectWizardVacanciesIsLoading,
    selectWizardVacancyFilters,
    setWizardSelectedFilterValues,
    WizardFilterOption,
} from 'store/models/campaignCreationWizard';
import { useDispatch, useSelector } from 'store/utils';

type FilterKey = 'areas' | 'users';

interface VacancyFilterProps {
    filterKey: FilterKey;
}

const TrlKeys = {
    apply: 'CampaignCreationWizard.VacancySelectStep.filter.apply',
    discard: 'CampaignCreationWizard.VacancySelectStep.filter.discard',
    placeholder: {
        areas: 'CampaignCreationWizard.VacancySelectStep.filter.region',
        users: 'CampaignCreationWizard.VacancySelectStep.filter.manager',
    },
};

export const VacancyFilter: FC<VacancyFilterProps> = ({ filterKey }) => {
    const dispatch = useDispatch();
    const trl = useTrls();

    const vacancyFilters = useSelector(selectWizardVacancyFilters);
    const selectedFilterValues = useSelector(selectWizardSelectedFilterValues);
    const isLoading = useSelector(selectWizardVacanciesIsLoading);

    const filterData = vacancyFilters[filterKey];
    const appliedValues = selectedFilterValues[filterKey];

    const [pendingValues, setPendingValues] = useState<WizardFilterOption[]>(appliedValues);

    const staticProvider = useMemo(() => createStaticDataProvider(filterData), [filterData]);

    if (!filterData.length) {
        return null;
    }

    const handleApply = () => {
        dispatch(setWizardSelectedFilterValues({ ...selectedFilterValues, [filterKey]: pendingValues }));
        void dispatch(fetchWizardVacancySearch({}));
    };

    const handleDiscard = () => {
        setPendingValues([]);
        dispatch(setWizardSelectedFilterValues({ ...selectedFilterValues, [filterKey]: [] }));
        void dispatch(fetchWizardVacancySearch({}));
    };

    const handleDropClose = () => {
        setPendingValues(appliedValues);
    };

    return (
        <SingleFilter
            name={filterKey}
            placeholder={trl(TrlKeys.placeholder[filterKey])}
            appliedValues={appliedValues}
            value={pendingValues}
            isLoading={isLoading}
            dataProvider={staticProvider}
            applyLabel={trl(TrlKeys.apply)}
            discardLabel={trl(TrlKeys.discard)}
            dataQaPrefix={`wizard-filter-${filterKey}`}
            onApply={handleApply}
            onClear={handleDiscard}
            onChange={setPendingValues}
            onSelectOption={setPendingValues}
            onDropClose={handleDropClose}
            onBottomSheetClose={handleDropClose}
        />
    );
};
